import { useEffect, useState, useCallback } from "react";
import {
  Radiation, MapPin, Star, Loader2, ShieldCheck, BadgeCheck, Search, Clock, ChevronRight, X,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import PageHeader from "@/components/PageHeader";
import SavePreferredButton from "@/components/SavePreferredButton";
import { diagnosticsApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useLocationStore } from "@/store/locationStore";

const MODALITIES = ["MRI", "CT", "USG", "X-RAY", "ECHO", "MAMMO", "PET-CT"];
const MODALITY_PRICE = { MRI: 4500, CT: 3500, USG: 1200, "X-RAY": 500, ECHO: 1800, MAMMO: 2000, "PET-CT": 18000 };
const priceFor = (m) => MODALITY_PRICE[(m || "").toUpperCase()] ?? 1000;
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function next7Days() {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return { key: d.toISOString().slice(0, 10), day: i === 0 ? "Today" : days[d.getDay()], date: `${d.getDate()}/${d.getMonth() + 1}`, iso: d.toISOString() };
  });
}

function CentreCard({ c, userId, onBook }) {
  return (
    <div className="relative rounded-3xl border border-border p-4 hover:shadow-md transition-all" data-testid={`diagnostic-centre-${c.id}`}>
      <div className="absolute top-3 right-3 z-10">
        <SavePreferredButton userId={userId} provider={{ id: c.id, name: c.name, city: c.city, state: c.state, address: c.address }} category="diagnostic" variant="icon" />
      </div>
      <div className="flex items-start gap-3 pr-8">
        <div className="h-11 w-11 rounded-2xl bg-pink-50 flex items-center justify-center shrink-0">
          <Radiation className="h-5 w-5 text-pink-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-semibold truncate">{c.name}</p>
            {c.is_verified && (
              <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                <BadgeCheck className="h-3 w-3" /> Verified by Trata
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">{c.address || c.city || "Diagnostic centre"}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-xs flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{(c.rating || 0).toFixed(1)} ({c.total_bookings || 0})</span>
            {c.distance_km != null && <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{c.distance_km.toFixed(1)} km</span>}
            {!!c.report_tat_hours && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />Report in {c.report_tat_hours}h</span>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {(c.modalities || []).slice(0, 6).map((m) => (
              <span key={m} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{m}</span>
            ))}
            {(c.accreditations || []).map((a) => (
              <span key={a} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{a}</span>
            ))}
          </div>
        </div>
      </div>
      <Button className="w-full mt-4 rounded-2xl" onClick={() => onBook(c)} data-testid={`book-diagnostic-${c.id}`}>
        Book Scan <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

export default function DiagnosticCentres() {
  const user = useAuthStore((s) => s.user);
  const coords = useLocationStore((s) => s.coords);
  const locLabel = useLocationStore((s) => s.label);
  const userId = user?._id || user?.id;

  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalityFilter, setModalityFilter] = useState(null);

  const [bookingCentre, setBookingCentre] = useState(null);
  const [selectedScans, setSelectedScans] = useState([]);
  const [bookDate, setBookDate] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { radius_km: 50 };
    if (coords?.latitude && coords?.longitude) { params.lat = coords.latitude; params.lng = coords.longitude; }
    diagnosticsApi
      .listCentres(params)
      .then((res) => setCentres(res.data?.centres || []))
      .catch(() => setCentres([]))
      .finally(() => setLoading(false));
  }, [coords?.latitude, coords?.longitude]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user) {
      setPatientName(user.name || user.fullName || "");
      setPatientPhone(user.phone || "");
    }
  }, [user]);

  const openBooking = (c) => {
    setBookingCentre(c);
    setSelectedScans([]);
    setBookDate(null);
  };
  const toggleScan = (m) => setSelectedScans((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));
  const bookingTotal = selectedScans.reduce((s, m) => s + priceFor(m), 0);

  const confirmBooking = async () => {
    if (!bookingCentre || selectedScans.length === 0 || !bookDate || !patientName.trim()) return;
    setSubmitting(true);
    try {
      const res = await diagnosticsApi.createBooking({
        diagnostic_centre_profile_id: bookingCentre.id,
        patient_id: userId,
        patient_name: patientName.trim(),
        patient_phone: patientPhone.trim(),
        scans: selectedScans.map((m) => ({ modality: m, body_part: null, price: priceFor(m) })),
        scheduled_for: bookDate,
        total_amount: bookingTotal,
      });
      const bn = res.data?.booking?.booking_number;
      setBookingCentre(null);
      toast.success(`Booking requested${bn ? ` (${bn})` : ""}. The centre will confirm shortly.`);
    } catch {
      toast.error("Could not book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const shown = centres
    .filter((c) => (!search.trim() || c.name.toLowerCase().includes(search.trim().toLowerCase())))
    .filter((c) => (!modalityFilter || (c.modalities || []).map((m) => m.toUpperCase()).includes(modalityFilter)));

  return (
    <div className="max-w-3xl" data-testid="diagnostic-centres-page">
      <PageHeader title="Diagnostic Centres" subtitle={`Imaging & diagnostics near ${locLabel || "you"}`} icon={Radiation} color="#DB2777" />

      <div className="relative mt-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9 rounded-2xl" placeholder="Search diagnostic centres..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="diagnostic-search-input" />
      </div>

      <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
        <button onClick={() => setModalityFilter(null)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap shrink-0 ${!modalityFilter ? "bg-foreground text-background border-foreground" : "bg-card border-border"}`} data-testid="modality-all">All</button>
        {MODALITIES.map((m) => (
          <button key={m} onClick={() => setModalityFilter((p) => (p === m ? null : m))} className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap shrink-0 ${modalityFilter === m ? "bg-foreground text-background border-foreground" : "bg-card border-border"}`} data-testid={`modality-${m}`}>{m}</button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {loading ? (
          [1, 2, 3].map((i) => <Skeleton key={i} className="h-40 w-full rounded-3xl" />)
        ) : shown.length === 0 ? (
          <Card className="rounded-3xl border-border flex flex-col items-center py-16 text-center" data-testid="diagnostics-empty">
            <Radiation className="h-12 w-12 text-muted-foreground/40" />
            <p className="mt-4 font-semibold text-muted-foreground">No diagnostic centres found</p>
            <p className="text-sm text-muted-foreground mt-1">Try changing filters or your location.</p>
          </Card>
        ) : (
          <>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#005C66]">
              <ShieldCheck className="h-4 w-4" /> {shown.length} centres
            </div>
            {shown.map((c) => <CentreCard key={c.id} c={c} userId={userId} onBook={openBooking} />)}
          </>
        )}
      </div>

      {/* Booking dialog */}
      {bookingCentre && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={() => setBookingCentre(null)}>
          <div className="bg-card w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="booking-dialog">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-lg truncate pr-3">{bookingCentre.name}</p>
              <button onClick={() => setBookingCentre(null)} data-testid="booking-close"><X className="h-5 w-5" /></button>
            </div>

            <p className="text-sm font-bold text-muted-foreground mb-2">Select scans / modalities</p>
            <div className="flex flex-wrap gap-2">
              {(bookingCentre.modalities || []).map((m) => {
                const active = selectedScans.includes(m);
                return (
                  <button key={m} onClick={() => toggleScan(m)} data-testid={`scan-${m}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
                    {m} <span className={`text-xs ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{money(priceFor(m))}</span>
                  </button>
                );
              })}
            </div>

            <p className="text-sm font-bold text-muted-foreground mt-5 mb-2">Preferred date</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {next7Days().map((d) => {
                const active = bookDate === d.iso;
                return (
                  <button key={d.key} onClick={() => setBookDate(d.iso)} data-testid={`date-${d.key}`}
                    className={`shrink-0 w-16 py-2 rounded-2xl border text-center ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>
                    <div className="text-xs font-semibold">{d.day}</div>
                    <div className="text-sm font-bold">{d.date}</div>
                  </button>
                );
              })}
            </div>

            <p className="text-sm font-bold text-muted-foreground mt-5 mb-2">Patient details</p>
            <Input className="rounded-2xl mb-2" placeholder="Full name" value={patientName} onChange={(e) => setPatientName(e.target.value)} data-testid="booking-name" />
            <Input className="rounded-2xl" placeholder="Phone number" value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} data-testid="booking-phone" />

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
              <span className="text-sm font-semibold text-muted-foreground">Estimated total</span>
              <span className="text-xl font-extrabold">{money(bookingTotal)}</span>
            </div>
            <Button className="w-full mt-4 rounded-2xl" disabled={selectedScans.length === 0 || !bookDate || !patientName.trim() || submitting} onClick={confirmBooking} data-testid="booking-confirm">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Booking"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
